// getUserLevelBenefitData.js
'use strict';
import { Map } from 'immutable';

import createCachedSelector, {
  createSelector,
} from '../resource/createCachedSelector.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';

import {
  PUBLIC_BADGE_URL_PATH,
  USER_LEVEL_BENEFIT_THRESHOLD,
} from '../RemoteConfigKeys.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import parseLevelThreshold from '../resource/parseLevelThreshold.js';
import getMinRequiredUserLevelByVipBenefitLevel from '../resource/getMinRequiredUserLevelByVipBenefitLevel.js';

const defaultMap = new Map();

/**
 * Get the starting level of a benefit
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} benefit - benefit name.
 * @return {number} The starting level of the benefit.
 */
export const getUserLevelBenefitStartingLevel = createCachedSelector(
  state => getUserLevelBenefitThresholdList(state),
  (state, benefit) => benefit,
  state => getAllUserLevelBenefitObject(state),
  (userLevelBenefitThreshold, benefit, allUserLevelBenefitObject) => {
    const benefitStartingLevel = Object.keys(
      allUserLevelBenefitObject
    ).findIndex(level => !!allUserLevelBenefitObject[level].includes(benefit));

    return getMinRequiredUserLevelByVipBenefitLevel({
      thresholdList: userLevelBenefitThreshold,
      vipBenefitLevel: benefitStartingLevel,
    });
  }
)((state, benefit) => `${benefit}`);

/**
 * Get the icon source of a benefit
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} benefit - benefit name.
 * @return {string} The icon source of the benefit.
 */
export const getUserLevelBenefitIconSource = createCachedSelector(
  (state, benefit) => benefit,
  (state, benefit) =>
    getRemoteConfigData(
      state,
      `USER_LEVEL_BENEFIT_ICON_${benefit?.toUpperCase()}`
    ),
  state => getRemoteConfigData(state, PUBLIC_BADGE_URL_PATH),
  (benefit, benefitIconFromRemoteConfig, publicBadgeUrlPath) => {
    const benefitIconSource = getResourceUrl({
      path: publicBadgeUrlPath,
      endpoint:
        benefitIconFromRemoteConfig || `/user_level_benefit_${benefit}.svg`,
      resourceType: PUBLIC,
    }).href;
    return benefitIconSource;
  }
)((state, benefit) => `${benefit}`);

/**
 * Get user level benefit object from remote config
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {object} Return user level benefit object. ex:
 * {
 *   0: [],
 *   1: ['benefit1', 'benefit2'],
 *   2: ['benefit1', 'benefit2', 'benefit3', 'benefit4'],
 *   ...
 * }
 */
export const getAllUserLevelBenefitObject = createSelector(
  state => state.get('remoteConfig'),
  (remoteConfig = defaultMap) => {
    const regexp = /USER_LEVEL_(?<level>[\d]+)_BENEFIT/;
    const result = {
      [0]: [],
    };
    remoteConfig.forEach((value = '', key) => {
      const execResult = regexp.exec(key);
      if (execResult) {
        const level = +execResult.groups.level;
        result[level] =
          value.split(' ').filter(a => !(a == null || a === '')) || [];
      }
    });
    return result;
  }
);

/**
 * Resolves the VIP benefit level tier for a given user level.
 *
 * This selector finds the threshold range that the given user level falls into,
 * then returns the corresponding VIP benefit level and the min/max user level required for that tier.
 *
 * Example:
 *   USER_LEVEL_BENEFIT_THRESHOLD = "1:1~10 2:11~20 3:21~30 4:31~40 ..."
 *
 *   getVipBenefitLevelThresholdByUserLevel(state, 22)
 *   // => {
 *   //   vipLevel: 3,
 *   //   minRequiredUserLevel: 21,
 *   //   maxAllowedUserLevel: 30
 *   // }
 *
 * @kind selector
 * @param {Immutable.Map} state - Root state.
 * @param {number} userLevel - The user level value used for threshold matching.
 * @return {{
 *   vipBenefitLevel: number | undefined,
 *   minRequiredUserLevel: number | undefined,
 *   maxAllowedUserLevel: number | undefined,
 * }}
 */
export const getVipBenefitLevelThresholdByUserLevel = createCachedSelector(
  state => getUserLevelBenefitThresholdList(state),
  (state, level) => +level,
  (userLevelBenefitThresholdList, level) => {
    const vipBenefitLevelIndex = userLevelBenefitThresholdList.findIndex(
      ({ min, max }) => level >= min && level <= max
    );

    const {
      level: vipBenefitLevel,
      min: minRequiredUserLevel,
      max: maxAllowedUserLevel,
    } = userLevelBenefitThresholdList[vipBenefitLevelIndex] ?? {};

    return {
      vipBenefitLevel,
      minRequiredUserLevel,
      maxAllowedUserLevel,
      vipBenefitLevelIndex,
    };
  }
)((state, level) => `${level}`);

/**
 * Parses USER_LEVEL_BENEFIT_THRESHOLD remote config into a structured list.
 *
 * This selector retrieves USER_LEVEL_BENEFIT_THRESHOLD from remote config
 * and converts the threshold definition string into an array of objects.
 *
 * Example:
 *   USER_LEVEL_BENEFIT_THRESHOLD = "1:1~10 2:11~20 3:21~30 ..."
 *   // =>
 *   [
 *     { level: 1, min: 1, max: 10 },
 *     { level: 2, min: 11, max: 20 },
 *     { level: 3, min: 21, max: 30 },
 *     ...
 *   ]
 *
 * @kind selector
 * @param {Object} state - Root redux state.
 * @return {Array<{ level: number, min: number, max: number }>}
 *   Parsed user level benefit threshold list.
 */
export const getUserLevelBenefitThresholdList = createSelector(
  state => getRemoteConfigData(state, USER_LEVEL_BENEFIT_THRESHOLD),
  userLevelBenefitThreshold =>
    parseLevelThreshold({ threshold: userLevelBenefitThreshold })
);
